/**
 * FilterSidebar — advanced filters for the results page.
 *
 * Airlines checklist · Stops · Price range · Departure time · Arrival time ·
 * Flight duration · Refundable · Baggage included. Fully controlled: parent owns the
 * `filters` object and receives updates via `onChange`. `bounds` supplies the min/max
 * values + available airlines derived from the current result set.
 *
 * @param {Object} props
 * @param {Object} props.filters
 * @param {(next:Object) => void} props.onChange
 * @param {{airlines:string[], priceMin:number, priceMax:number, durationMin:number, durationMax:number}} props.bounds
 * @param {() => void} props.onReset
 */
import React from "react";
import "./filter-sidebar.css";

export const TIME_BUCKETS = [
  { key: "morning", label: "Morning", hint: "5a–12p", icon: "fas fa-cloud-sun" },
  { key: "afternoon", label: "Afternoon", hint: "12p–5p", icon: "fas fa-sun" },
  { key: "evening", label: "Evening", hint: "5p–9p", icon: "fas fa-cloud-moon" },
  { key: "night", label: "Night", hint: "9p–5a", icon: "fas fa-moon" },
];

export const STOP_OPTIONS = [
  { key: "nonstop", label: "Nonstop" },
  { key: "1stop", label: "1 Stop" },
  { key: "2plus", label: "2 Stops+" },
];

const money = (n) => `PKR ${Math.round(n).toLocaleString("en-PK")}`;
const hrs = (mins) => `${Math.floor(mins / 60)}h ${mins % 60}m`;

export default function FilterSidebar({ filters, onChange, bounds, onReset }) {
  const toggleArray = (field, value) => {
    const set = new Set(filters[field]);
    set.has(value) ? set.delete(value) : set.add(value);
    onChange({ ...filters, [field]: Array.from(set) });
  };

  const set = (patch) => onChange({ ...filters, ...patch });

  return (
    <aside className="fsb">
      <div className="fsb__header">
        <h3 className="fsb__title">
          <i className="fas fa-sliders-h" aria-hidden="true" /> Filters
        </h3>
        <button type="button" className="fsb__reset" onClick={onReset}>
          Reset
        </button>
      </div>

      {/* Stops */}
      <section className="fsb__group">
        <h4 className="fsb__group-title">Stops</h4>
        {STOP_OPTIONS.map((s) => (
          <label className="fsb__check" key={s.key}>
            <input
              type="checkbox"
              checked={filters.stops.includes(s.key)}
              onChange={() => toggleArray("stops", s.key)}
            />
            <span>{s.label}</span>
          </label>
        ))}
      </section>

      {/* Airlines */}
      <section className="fsb__group">
        <h4 className="fsb__group-title">Airlines</h4>
        <div className="fsb__scroll">
          {bounds.airlines.map((name) => (
            <label className="fsb__check" key={name}>
              <input
                type="checkbox"
                checked={filters.airlines.includes(name)}
                onChange={() => toggleArray("airlines", name)}
              />
              <span>{name}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Price range */}
      <section className="fsb__group">
        <h4 className="fsb__group-title">Price Range</h4>
        <div className="fsb__range-values">
          <span>{money(filters.priceMin)}</span>
          <span>{money(filters.priceMax)}</span>
        </div>
        <div className="fsb__dual">
          <input
            type="range"
            min={bounds.priceMin}
            max={bounds.priceMax}
            value={filters.priceMin}
            onChange={(e) =>
              set({ priceMin: Math.min(Number(e.target.value), filters.priceMax) })
            }
          />
          <input
            type="range"
            min={bounds.priceMin}
            max={bounds.priceMax}
            value={filters.priceMax}
            onChange={(e) =>
              set({ priceMax: Math.max(Number(e.target.value), filters.priceMin) })
            }
          />
        </div>
      </section>

      {/* Departure time */}
      <section className="fsb__group">
        <h4 className="fsb__group-title">Departure Time</h4>
        <div className="fsb__buckets">
          {TIME_BUCKETS.map((b) => (
            <button
              type="button"
              key={b.key}
              className={`fsb__bucket ${
                filters.departureBuckets.includes(b.key) ? "fsb__bucket--active" : ""
              }`}
              onClick={() => toggleArray("departureBuckets", b.key)}
            >
              <i className={b.icon} aria-hidden="true" />
              <span className="fsb__bucket-label">{b.label}</span>
              <span className="fsb__bucket-hint">{b.hint}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Arrival time */}
      <section className="fsb__group">
        <h4 className="fsb__group-title">Arrival Time</h4>
        <div className="fsb__buckets">
          {TIME_BUCKETS.map((b) => (
            <button
              type="button"
              key={b.key}
              className={`fsb__bucket ${
                filters.arrivalBuckets.includes(b.key) ? "fsb__bucket--active" : ""
              }`}
              onClick={() => toggleArray("arrivalBuckets", b.key)}
            >
              <i className={b.icon} aria-hidden="true" />
              <span className="fsb__bucket-label">{b.label}</span>
              <span className="fsb__bucket-hint">{b.hint}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Duration */}
      <section className="fsb__group">
        <h4 className="fsb__group-title">Max Flight Duration</h4>
        <div className="fsb__range-values">
          <span>Up to</span>
          <span>{hrs(filters.durationMax)}</span>
        </div>
        <input
          type="range"
          className="fsb__single"
          min={bounds.durationMin}
          max={bounds.durationMax}
          value={filters.durationMax}
          onChange={(e) => set({ durationMax: Number(e.target.value) })}
        />
      </section>

      {/* Booleans */}
      <section className="fsb__group">
        <label className="fsb__check">
          <input
            type="checkbox"
            checked={filters.refundable}
            onChange={(e) => set({ refundable: e.target.checked })}
          />
          <span>Refundable only</span>
        </label>
        <label className="fsb__check">
          <input
            type="checkbox"
            checked={filters.baggageIncluded}
            onChange={(e) => set({ baggageIncluded: e.target.checked })}
          />
          <span>Baggage included</span>
        </label>
      </section>
    </aside>
  );
}
